import { useEffect } from "react";
import { api, type ReadingResponse, type UserView } from "../api";
import { ASSISTANT_NAME } from "../brand";
import { DailyCardWidget } from "../components/DailyCardWidget";
import { BrandMark, IconChat, IconGift, IconPerson } from "../icons";

export type ForecastPeriod = "today" | "tomorrow" | "month" | "year";

/** Классические темы личного года (1-9) — общепринятая нумерологическая формула, не из базы курса. */
const PERSONAL_YEAR_THEMES: Record<number, string> = {
  1: "год начала",
  2: "год сотрудничества",
  3: "год самовыражения",
  4: "год труда и порядка",
  5: "год перемен",
  6: "год ответственности",
  7: "год осмысления",
  8: "год результатов",
  9: "год завершения",
};

interface HomeCard {
  id: string;
  title: string;
  subtitle: string;
  thumb: string;
}

const CARDS: HomeCard[] = [
  { id: "reading", title: "Мой разбор", subtitle: "Полный анализ по дате рождения", thumb: "/assets/cards/my-reading.webp" },
  // TODO: заменить на отдельную картинку для матрицы судьбы — пока временно переиспользуем money.webp
  { id: "matrix", title: "Матрица судьбы", subtitle: "22 аркана по дате рождения — бесплатно", thumb: "/assets/cards/money.webp" },
  { id: "forecast", title: "Прогнозы", subtitle: "День, месяц, год", thumb: "/assets/cards/forecasts.webp" },
  { id: "compat", title: "Совместимость", subtitle: "Любовь, семья, партнёрство", thumb: "/assets/cards/compatibility.webp" },
  { id: "money", title: "Деньги и реализация", subtitle: "Твой финансовый код", thumb: "/assets/cards/money.webp" },
  { id: "energy", title: "Энергия и здоровье", subtitle: "Твой ресурс и баланс", thumb: "/assets/cards/energy.webp" },
  { id: "tarot", title: "Таро и подсказки", subtitle: "Ответы на важные вопросы", thumb: "/assets/cards/tarot.webp" },
  { id: "tarot78", title: "Таро на 78 карт", subtitle: "Классический расклад полной колодой", thumb: "/assets/cards/tarot.webp" },
  { id: "palm", title: "Разбор ладони", subtitle: "Что написано на твоей руке", thumb: "/assets/cards/palm-hand.png" },
  // TODO: заменить на отдельную картинку для натальной карты — пока временно переиспользуем tarot.webp
  { id: "natal", title: "Натальная карта", subtitle: "Планеты в момент твоего рождения", thumb: "/assets/cards/tarot.webp" },
];

interface Props {
  user: UserView;
  onProfile: () => void;
  onCard: (id: string) => void;
  onGift: () => void;
  onDayLoaded: (r: ReadingResponse) => void;
  onOpenDayFull: () => void;
}

const WEEKDAYS = ["ВОСКРЕСЕНЬЕ", "ПОНЕДЕЛЬНИК", "ВТОРНИК", "СРЕДА", "ЧЕТВЕРГ", "ПЯТНИЦА", "СУББОТА"];
const MONTHS = ["ЯНВАРЯ", "ФЕВРАЛЯ", "МАРТА", "АПРЕЛЯ", "МАЯ", "ИЮНЯ", "ИЮЛЯ", "АВГУСТА", "СЕНТЯБРЯ", "ОКТЯБРЯ", "НОЯБРЯ", "ДЕКАБРЯ"];

function todayLabel(): string {
  const d = new Date();
  return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** Главная: бренд-шапка, hero-фото, приветствие, карта дня, CTA, сетка из карточек, баннер подарка. */
export function Home({ user, onProfile, onCard, onGift, onDayLoaded, onOpenDayFull }: Props) {
  useEffect(() => {
    let alive = true;
    // Фоново фиксируем просмотр «числа дня» для стрика/монет — карточка на главной больше не показывается, но событие нужно сохранить.
    api.reading("forecast", { period: "today" }).then((r) => alive && onDayLoaded(r), () => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="warm homepage">
      <div className="homehero">
        <div className="herobrand">
          <BrandMark className="brandicon" />
          <div className="brandtext">
            <b>
              Код
              <br />
              Масштаба
            </b>
            <span>
              Цифровая психология
              <br />
              для жизни в балансе
            </span>
          </div>
        </div>
        <p className="heroscript">
          Ты больше,
          <br />
          чем цифры ♡
        </p>
        <button className="roundbtn heroprofile" onClick={onProfile} aria-label="Профиль">
          <IconPerson />
        </button>
      </div>

      <div className="homegreeting">
        <span className="homegreeting-date">{todayLabel()}</span>
        <p className="homegreeting-name">Привет{user.firstName ? `, ${user.firstName}` : ""}</p>
        {user.personalYear && (
          <span className="homegreeting-year">
            {user.personalYear}-й личный год: {PERSONAL_YEAR_THEMES[user.personalYear]}
          </span>
        )}
      </div>

      <DailyCardWidget firstName={user.firstName} onOpenFull={onOpenDayFull} />

      <button className="asklink" onClick={() => onCard("chat")}>
        <IconChat className="asklink-ico" />
        <span className="asklink-text">
          <b>Спросить {ASSISTANT_NAME}</b>
          <span>Любой личный вопрос: про него, деньги, решение. {ASSISTANT_NAME} ответит голосом.</span>
        </span>
        <span className="arrow">›</span>
      </button>

      <div className="herocta">
        <button className="homecta" onClick={() => onCard("reading")}>
          <IconPerson />
          <span>Построить мой разбор</span>
          <span aria-hidden>→</span>
        </button>
        <p className="homecta-hint">Узнай, какие энергии заложены в тебе по дате рождения и получи персональные рекомендации</p>
      </div>

      <div className="gridcards">
        {CARDS.map((c) => (
          <button key={c.id} className="gridcard" onClick={() => onCard(c.id)} aria-label={`${c.title}. ${c.subtitle}`}>
            <img className="thumb" src={c.thumb} alt="" loading="lazy" />
            <span className="dailycard-ambient a1" aria-hidden>✦</span>
            <span className="dailycard-ambient a2" aria-hidden>✦</span>
          </button>
        ))}
      </div>

      <button className="giftbanner" onClick={onGift}>
        <IconGift className="giftico" />
        <span className="gifttext">
          <b>Твой подарок</b>
          <span>Мини-разбор бесплатно</span>
        </span>
        <span className="arrow">›</span>
      </button>
    </div>
  );
}
